import { useEffect, useRef, useState, type SetStateAction } from "react";
import { Plus, Minus } from "lucide-react";
import GameTopBar from "./GameTopBar";
import Result from "./Result";

interface IOppDetails {
  userId: string;
  username: string;
}

interface IMemoryMainContent{
  questions: number[][] | null;
  websocket: WebSocket | null;
  rating: number;
  score: number;
  oppScore: number;
  setScore: React.Dispatch<SetStateAction<number>>;
  setOppScore: React.Dispatch<SetStateAction<number>>;
  oppPlayerDetails: IOppDetails | null;
  oppRating: number;
}

function MemoryMainContent({
  questions, websocket, rating, score, oppScore, setScore, setOppScore, oppPlayerDetails, oppRating
}: IMemoryMainContent) {
  const [digitStr, setDigitStr] = useState<string>("1");
  const [isDigitSet, setIsDigitSet] = useState<boolean>(false);
  const [speedStr, setSpeedStr] = useState<string>("1500"); // ms
  const [timer, setTimer] = useState<number>(10);
  const [digitToShow, setDigitToShow] = useState<number | null>(0);
  const [showAnswerBox, setShowAnswerBox] = useState<boolean>(false);
  const [ans, setAns] = useState<string>("0");
  const [ansTimer, setAnsTimer] = useState<number>(5);
  const [showResult, setShowResult] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const ansRef = useRef<string>(ans);

  const indexRef = useRef(0);

  // Keep ref mapped to fresh state
  useEffect(() => {
    ansRef.current = ans;
  }, [ans]);

  // Setup Phase Timer
  useEffect(() => {
    if (isDigitSet) return;
    const timerId = setInterval(() => {
      setTimer((t) => {
        if(t > 0) return t - 1;
        setIsDigitSet(true);
        return 0;
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [isDigitSet]);

  // Memorization Phase
  useEffect(() => {
    if (!isDigitSet || !questions || showAnswerBox) return;

    let actualSpeed = parseInt(speedStr) || 1500;
    if (actualSpeed < 1000) actualSpeed = 1000;
    if (actualSpeed > 5000) actualSpeed = 5000;

    let actualLength = parseInt(digitStr) || 1;
    if (actualLength < 1) actualLength = 1;
    if (actualLength > 5) actualLength = 5;

    // Show first digit immediately
    if (indexRef.current === 0 && questions.length > 0) {
      setDigitToShow(questions[0]![actualLength - 1]!);
      indexRef.current = 1;
    }

    const interval = setInterval(() => {
      setDigitToShow(null); // blank out to create a distinct blink
      
      if (indexRef.current >= questions.length) {
        setTimeout(() => setShowAnswerBox(true), 150);
        clearInterval(interval);
        return;
      }
      
      setTimeout(() => {
        setDigitToShow(questions[indexRef.current]![actualLength - 1]!);
        indexRef.current++;
      }, 150);

    }, actualSpeed);

    return () => clearInterval(interval);
  }, [isDigitSet, speedStr, digitStr, questions, showAnswerBox]);

  // Answer Phase Timer
  useEffect(() => {
    if (!showAnswerBox || showResult) return;
    inputRef.current?.focus(); // auto focus input

    const timerId = setInterval(() => {
      setAnsTimer((t) => {
        if(t <= 1) {
          clearInterval(timerId);
          processSubmit("0");
          setShowResult(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [showAnswerBox, showResult]); 

  function submitAnswerFromRef() {
    processSubmit(ansRef.current);
  }

  function submitAnswer(e?: React.FormEvent) {
    if(e) e.preventDefault();
    processSubmit(ans);
  }

  function processSubmit(finalAns: string) {
    if(!finalAns) return;

    let safeDigit = parseInt(digitStr) || 1;
    if (safeDigit < 1) safeDigit = 1;
    if (safeDigit > 5) safeDigit = 5;

    websocket?.send(JSON.stringify({
      type: "memory",
      rating: rating,
      ans: finalAns,
      digit: safeDigit
    }));

    websocket!.onmessage = (msg) => {
      const parse = JSON.parse(msg.data);
      if (parse.score !== undefined) {
        setScore(parse.score);
      }
      
      if (parse.yourScore !== undefined && parse.oppScore !== undefined) {
        setScore(parse.yourScore);
        setOppScore(parse.oppScore);
        if (parse.yourScore === 100) {
          setShowResult(true);
        }
      }
    };
    
    setAns("0");
  }

  function handleDigitChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (val === "") {
      setDigitStr("");
      return;
    }
    const num = parseInt(val);
    if (!isNaN(num)) {
      if (num > 5) setDigitStr("5");
      else if (num < 1) setDigitStr("1");
      else setDigitStr(num.toString());
    }
  }

  function handleDigitBlur() {
    const num = parseInt(digitStr);
    if (isNaN(num) || num < 1) setDigitStr("1");
  }

  function handleSpeedChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (val === "") {
      setSpeedStr("");
      return;
    }
    const num = parseInt(val);
    if (!isNaN(num)) {
      if (num > 5000) {
        setSpeedStr("5000");
      } else {
        setSpeedStr(val);
      }
    }
  }

  function handleSpeedBlur() {
    const num = parseInt(speedStr);
    if (isNaN(num) || num < 1000) setSpeedStr("1000");
  }

  function handleDigitBtn(delta: number) {
    let num = parseInt(digitStr);
    if (isNaN(num)) num = 1;
    num += delta;
    if (num < 1) num = 1;
    if (num > 5) num = 5;
    setDigitStr(num.toString());
  }

  function handleSpeedBtn(delta: number) {
    let num = parseInt(speedStr);
    if (isNaN(num)) num = 1500;
    num += delta;
    if (num < 1000) num = 1000;
    if (num > 5000) num = 5000;
    setSpeedStr(num.toString());
  }

  if (showResult) {
    return (
      <div className="flex-1 overflow-hidden h-full w-full bg-[#0a0a12]">
        <Result score={score} oppScore={oppScore} />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#0a0a12] overflow-hidden relative w-full h-full">
      <GameTopBar
        timer={!isDigitSet ? timer : showAnswerBox ? ansTimer : 0}
        userRating={rating}
        oppUsername={oppPlayerDetails?.username || "OPP"}
        oppRating={oppRating || 0}
        score={score}
        oppScore={oppScore}
        hideScore={true}
        hideTimer={true}
      />

      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 w-full h-full relative">
        
        {/* Phase 3: Answering */}
        {showAnswerBox && isDigitSet ? (
          <div className="w-full max-w-sm bg-[#101020]/90 backdrop-blur-md border border-violet-500/20 rounded-[24px] p-6 sm:p-8 flex flex-col items-center animate-[fade-in-up_0.5s_ease_both] shadow-2xl">
            <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-widest mb-1">Your Turn</h2>
            <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-6">Time remaining: <span className="text-red-400">{ansTimer}s</span></p>

            <h3 className="text-xs sm:text-[13px] font-bold text-violet-400 uppercase tracking-widest mb-4">Type the pattern</h3>
            <form className="w-full flex flex-col gap-4" onSubmit={submitAnswer}>
              <input 
                ref={inputRef}
                type="number" 
                onChange={(e) => setAns(e.target.value)} 
                placeholder="YOUR ANSWER"
                className="w-full py-4 text-center bg-[#0a0a12]/50 border border-white/10 rounded-2xl text-[24px] font-black text-white tracking-[0.2em] outline-none focus:border-violet-500 transition-colors placeholder:text-gray-600/50 placeholder:text-lg placeholder:font-bold placeholder:tracking-widest [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]"
                autoComplete="off"
              />
              <button 
                type="submit" 
                className="w-full py-3.5 mt-2 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-xl text-white font-bold text-[15px] uppercase tracking-widest hover:-translate-y-0.5 active:translate-y-0 transition-transform shadow-[0_6px_20px_rgba(139,92,246,0.3)]"
              >
                Submit
              </button>
            </form>
          </div>
        ) 
        
        /* Phase 2: Memorizing */
        : isDigitSet ? (
          <div className="flex items-center justify-center flex-1 w-full h-full">
            {digitToShow !== null && (
              <h3 
                key={indexRef.current}
                className="text-[120px] sm:text-[160px] font-black text-[#f1f0ff] leading-none drop-shadow-[0_0_40px_rgba(139,92,246,0.6)] animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]"
              >
                {digitToShow}
              </h3>
            )}
          </div>
        ) 
        
        /* Phase 1: Setup Configuration */
        : (
          <div className="w-full max-w-sm bg-[#101020]/90 backdrop-blur-md border border-violet-500/20 rounded-[24px] p-6 sm:p-8 flex flex-col items-center animate-[fade-in-up_0.5s_ease_both] shadow-2xl">
            <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-widest mb-1">Configuration</h2>
            <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-8">Match starts in <span className="text-red-400">{timer}s</span></p>
            
            <div className="w-full flex flex-col gap-6">
              {/* Digit Control */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <label className="text-[11px] font-bold text-violet-400 uppercase tracking-widest">Select Digits</label>
                  <span className="text-[10px] text-gray-500 font-medium">Max 5</span>
                </div>
                <div className="flex items-center gap-3 w-full">
                  <button 
                    onClick={() => handleDigitBtn(-1)}
                    disabled={parseInt(digitStr) <= 1 || isNaN(parseInt(digitStr))}
                    type="button"
                    className="w-12 h-12 flex shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 hover:border-violet-400/30 active:scale-95 disabled:opacity-40 disabled:hover:bg-violet-500/10 disabled:hover:border-violet-500/20 disabled:active:scale-100 disabled:cursor-not-allowed transition-all"
                  >
                    <Minus size={20} strokeWidth={2.5} />
                  </button>
                  <input 
                    type="number" 
                    max={5} min={1} 
                    value={digitStr}
                    onChange={handleDigitChange}
                    onBlur={handleDigitBlur}
                    className="flex-1 w-full py-3 text-center bg-[#0a0a12]/50 border border-white/10 rounded-xl text-[18px] font-bold text-white outline-none focus:border-violet-500 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]"
                  />
                  <button 
                    onClick={() => handleDigitBtn(1)}
                    disabled={parseInt(digitStr) >= 5}
                    type="button"
                    className="w-12 h-12 flex shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 hover:border-violet-400/30 active:scale-95 disabled:opacity-40 disabled:hover:bg-violet-500/10 disabled:hover:border-violet-500/20 disabled:active:scale-100 disabled:cursor-not-allowed transition-all"
                  >
                    <Plus size={20} strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Speed Control */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <label className="text-[11px] font-bold text-violet-400 uppercase tracking-widest">Flash Speed</label>
                  <span className="text-[10px] text-gray-500 font-medium">Faster = Harder</span>
                </div>
                <div className="flex items-center gap-3 w-full">
                  <button 
                    onClick={() => handleSpeedBtn(-100)}
                    disabled={parseInt(speedStr) <= 1500 || isNaN(parseInt(speedStr))}
                    type="button"
                    className="w-12 h-12 flex shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 hover:border-violet-400/30 active:scale-95 disabled:opacity-40 disabled:hover:bg-violet-500/10 disabled:hover:border-violet-500/20 disabled:active:scale-100 disabled:cursor-not-allowed transition-all"
                  >
                    <Minus size={20} strokeWidth={2.5} />
                  </button>
                  <input 
                    type="number" 
                    min={1500} max={5000} step={100}
                    value={speedStr}
                    onChange={handleSpeedChange}
                    onBlur={handleSpeedBlur}
                    className="flex-1 w-full py-3 text-center bg-[#0a0a12]/50 border border-white/10 rounded-xl text-[18px] font-bold text-white outline-none focus:border-violet-500 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]"
                  />
                  <button 
                    onClick={() => handleSpeedBtn(100)}
                    disabled={parseInt(speedStr) >= 5000}
                    type="button"
                    className="w-12 h-12 flex shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 hover:border-violet-400/30 active:scale-95 disabled:opacity-40 disabled:hover:bg-violet-500/10 disabled:hover:border-violet-500/20 disabled:active:scale-100 disabled:cursor-not-allowed transition-all"
                  >
                    <Plus size={20} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* <div className="mt-8 text-center bg-violet-500/10 border border-violet-500/20 px-4 py-2 rounded-xl">
              <span className="text-[10px] font-bold text-violet-300">Wait for countdown to begin!</span>
            </div> */}
          </div>
        )}

      </div>
    </div>
  );
}

export default MemoryMainContent;
